import { useCallback, useEffect, useMemo } from "react";

import { MenuDivider, MenuItem } from "@blueprintjs/core";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router";

import { ExpiresOnCell } from "@/components/pages_logged_in/certifications/ExpiresOnCell";
import { IssuerCell } from "@/components/pages_logged_in/certifications/IssuerCell";
import { SortableHeader } from "@/components/pages_logged_in/certifications/SortableHeader";
import { UserCell } from "@/components/pages_logged_in/certifications/UserCell";
import { MoreMenu } from "@/components/pieces/more/MoreMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CertificationParticipantsQuery,
  ExpirationType,
  useCertificationParticipantsSuspenseQuery,
} from "@/graphql";

const getColumns: (expirationType: ExpirationType) => ColumnDef<
  NonNullable<
    CertificationParticipantsQuery["certification"]
  >["participants"]["data"][number] & {
    revoke: () => void;
    viewPdf: () => void;
  }
>[] = (expirationType: ExpirationType) => {
  return [
    {
      accessorKey: "name",
      sortDescFirst: false,
      header: ({ column }) => {
        return (
          <SortableHeader
            text="Name"
            direction={column.getIsSorted()}
            onClick={column.getToggleSortingHandler()}
            iconType="alpha"
          />
        );
      },
      cell: ({ row }) => {
        return <UserCell user={row.original.user} />;
      },
    },
    {
      accessorKey: "awardedAt",
      header: ({ column }) => {
        return (
          <SortableHeader
            text="Awarded On"
            direction={column.getIsSorted()}
            onClick={column.getToggleSortingHandler()}
            iconType="date"
          />
        );
      },
      cell: ({ row }) => {
        return (
          <div>{format(new Date(row.original.awardedAt), "MMM d, yyyy")}</div>
        );
      },
      sortingFn: (rowA, rowB) => {
        return (
          new Date(rowA.original.awardedAt).getTime() -
          new Date(rowB.original.awardedAt).getTime()
        );
      },
    },
    {
      accessorKey: "expiresAt",
      header: ({ column }) => {
        if (expirationType === ExpirationType.Relative) {
          return (
            <SortableHeader
              text="Expires On"
              direction={column.getIsSorted()}
              onClick={column.getToggleSortingHandler()}
              iconType="date"
            />
          );
        } else {
          return <div>Expires On</div>;
        }
      },
      cell: ({ row }) => {
        return <ExpiresOnCell date={row.original.expiresAt} />;
      },
      sortingFn: (rowA, rowB) => {
        if (rowA.original.expiresAt && rowB.original.expiresAt) {
          return (
            new Date(rowA.original.expiresAt).getTime() -
            new Date(rowB.original.expiresAt).getTime()
          );
        }
        return 0;
      },
    },
    {
      accessorKey: "issuedBy",
      header: ({ column }) => {
        return (
          <SortableHeader
            text="Issued By"
            direction={column.getIsSorted()}
            onClick={column.getToggleSortingHandler()}
            iconType="alpha"
          />
        );
      },
      cell: ({ row }) => {
        return <IssuerCell participant={row.original} />;
      },
      sortDescFirst: false,
    },
    {
      accessorKey: "actions",
      header: () => <div className="flex justify-end">Actions</div>,
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <MoreMenu>
              <MenuItem text="View PDF" onClick={row.original.viewPdf} />
              <MenuDivider />
              <MenuItem
                text="Revoke"
                intent="danger"
                onClick={row.original.revoke}
              />
            </MoreMenu>
          </div>
        );
      },
    },
  ];
};

export function CertificationParticipantsTable({
  search,
  selectedGroupIds,
  page,
  pageSize,
  sort,
  onSortChange,
  onRevoke,
  onTotalChange,
}: {
  search: string;
  selectedGroupIds: string[];
  page: number;
  pageSize: number;
  sort: string | null;
  onSortChange: (sort: string | null) => void;
  onRevoke: (certificateId: string) => void;
  onTotalChange: (total: number) => void;
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const groupIds =
    selectedGroupIds.length === 1 && selectedGroupIds[0] === "all"
      ? undefined
      : selectedGroupIds;
  const { data } = useCertificationParticipantsSuspenseQuery({
    variables: {
      id: id!,
      page,
      size: pageSize,
      name: search,
      groupIds,
      sort,
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    onTotalChange(data?.certification?.participants?.total ?? 0);
  }, [data?.certification?.participants?.total]);

  const tableData = useMemo(
    () =>
      data?.certification?.participants?.data?.map((participant) => ({
        ...participant,
        viewPdf: () => {
          navigate(`/certificates/${participant.id}`);
        },
        revoke: () => {
          onRevoke(participant.id);
        },
      })) ?? [],
    [data?.certification?.participants?.data, navigate, onRevoke],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const sorting =
        updaterOrValue instanceof Function
          ? updaterOrValue(
              sort
                ? [
                    {
                      id: sort.startsWith("-") ? sort.slice(1) : sort,
                      desc: sort.startsWith("-"),
                    },
                  ]
                : [],
            )
          : updaterOrValue;
      onSortChange(
        sorting.length > 0
          ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
          : null,
      );
    },
    [sort, onSortChange],
  );

  const state = useMemo(() => {
    return {
      sorting: sort
        ? [
            {
              id: sort.startsWith("-") ? sort.slice(1) : sort,
              desc: sort.startsWith("-"),
            },
          ]
        : [],
    };
  }, [sort]);

  const columns = useMemo(
    () =>
      getColumns(
        data?.certification?.expirationType ?? ExpirationType.NoExpiration,
      ),
    [data?.certification?.expirationType],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: handleSortingChange,
    manualSorting: true,
    state,
  });
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center h-40">
                {search &&
                groupIds &&
                groupIds.length > 0 &&
                groupIds[0] !== "all"
                  ? `No certified members found with name containing "${search}" in the selected groups`
                  : groupIds && groupIds.length > 0 && groupIds[0] !== "all"
                    ? `No certified members found in the selected groups`
                    : search
                      ? `No certified members found with name containing "${search}"`
                      : `No certified members`}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
